import { RoomTypeID } from '@kamernet/core/ApiClient/Kamernet';
import { Locale } from '@kamernet/core/Intl/types';

import { defineRoute } from '../defineRoute';

export type ListingDetailsRouteParams = {
  listingID: number;
  listingType: RoomTypeID;
  city: string;
  street: string;
  psaIdentifier?: string | null;
};

export type ListingDetailsRouteParamsString = {
  searchCategoryAndCity: string;
  street: string;
  listingTypeAndID: string;
  psaIdentifier?: string;
};

const LISTING_TYPE_TO_STRING_EN: Record<RoomTypeID, string> = {
  [RoomTypeID.Room]: 'room',
  [RoomTypeID.Apartment]: 'apartment',
  [RoomTypeID.Studio]: 'studio',
  [RoomTypeID.Antikraak]: 'anti-squat',
  [RoomTypeID.StudentResidence]: 'studenthouse',
};

const LISTING_TYPE_TO_STRING_NL: Record<RoomTypeID, string> = {
  [RoomTypeID.Room]: 'kamer',
  [RoomTypeID.Apartment]: 'appartement',
  [RoomTypeID.Studio]: 'studio',
  [RoomTypeID.Antikraak]: 'anti-kraak',
  [RoomTypeID.StudentResidence]: 'studentenwoning',
};

const STRING_TO_LISTING_TYPE_EN: Record<string, RoomTypeID> = {
  room: RoomTypeID.Room,
  apartment: RoomTypeID.Apartment,
  studio: RoomTypeID.Studio,
  'anti-squat': RoomTypeID.Antikraak,
};

const STRING_TO_LISTING_TYPE_NL: Record<string, RoomTypeID> = {
  kamer: RoomTypeID.Room,
  appartement: RoomTypeID.Apartment,
  studio: RoomTypeID.Studio,
  'anti-kraak': RoomTypeID.Antikraak,
};

export const ListingDetails = defineRoute<
  ListingDetailsRouteParams,
  ListingDetailsRouteParamsString
>({
  pathname: '/for-rent/[searchCategoryAndCity]/[street]/[listingTypeAndID]',
  nlPathname: '/huren/[searchCategoryAndCity]/[street]/[listingTypeAndID]',
  options: { isLandlordRoute: false, isPublic: true },
  routeParamsStringifier: (routeParams, locale) => {
    const listingTypeString =
      locale === Locale.en
        ? LISTING_TYPE_TO_STRING_EN[routeParams.listingType]
        : LISTING_TYPE_TO_STRING_NL[routeParams.listingType];

    return {
      searchCategoryAndCity: `${listingTypeString}-${routeParams.city}`,
      street: routeParams.street,
      listingTypeAndID: `${listingTypeString}-${routeParams.listingID}`,
      psaIdentifier: routeParams.psaIdentifier ?? undefined,
    };
  },
  routeParamsParser: (routeParamsString, locale) => {
    let city: string;
    let listingType: RoomTypeID | null = null;
    let listingTypeString: string | null = null;

    for (const [key, value] of Object.entries(
      locale === Locale.en
        ? STRING_TO_LISTING_TYPE_EN
        : STRING_TO_LISTING_TYPE_NL,
    )) {
      if (routeParamsString.searchCategoryAndCity.startsWith(key)) {
        listingTypeString = key;
        listingType = value;
      }
    }

    try {
      if (!listingType) {
        throw new Error('listing type is not valid');
      }

      city = routeParamsString.searchCategoryAndCity
        .replace(`${listingTypeString}-`, '')
        .trim()
        .toLowerCase();

      if (!city) {
        throw new Error('city can not be empty');
      }
    } catch {
      city = '';
      listingType = null;
    }

    let listingID = 0;
    const listingIDMatch = routeParamsString.listingTypeAndID.match(/\d+/);

    if (listingIDMatch) {
      listingID = parseInt(listingIDMatch[0], 10);
    }

    return {
      listingID,
      city,
      listingType: listingType || RoomTypeID.Room, // @todo: use RoomTypeID.Empty
      street: routeParamsString.street,
      psaIdentifier: routeParamsString.psaIdentifier || '',
    };
  },
});
